/**
 * Impersonation store (A2).
 *
 * Holds the short-lived impersonation JWT minted by
 * POST /v1/superadmin/impersonate/start. While an impersonation is active the
 * api() client sends this token (so the operator sees the target user's data),
 * BUT the operator's own Supabase session stays intact — the impersonate/stop
 * call is super_admin + step-up gated and must travel on the operator's real
 * session (see `api(..., { useOperatorSession: true })`).
 *
 * sessionStorage-backed so it survives an in-tab reload but never outlives the
 * browser tab — an operator should never resume an impersonation after closing
 * the window. The 15-minute `exp` on the JWT is the hard backstop.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface ActiveImpersonation {
  /** The impersonation JWT (HS256, carries sub=target + impersonatedBy=operator). */
  jwt: string;
  targetUserId: string;
  /** Human display name of the impersonated user — shown in the banner. */
  targetDisplay: string;
  /** ISO timestamp the impersonation JWT expires. */
  expiresAt: string;
}

interface ImpersonationState {
  active: ActiveImpersonation | null;
  setImpersonation: (a: ActiveImpersonation) => void;
  clear: () => void;
}

export const useImpersonationStore = create<ImpersonationState>()(
  persist(
    (set) => ({
      active: null,
      setImpersonation: (active) => set({ active }),
      clear: () => set({ active: null }),
    }),
    {
      name: 'elreign:impersonation:v1',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

/**
 * Non-React accessor for the api() client. Returns the active impersonation
 * JWT when present and unexpired, else null. Expired tokens are cleared so the
 * banner drops and the client falls back to the operator's own session.
 */
export function activeImpersonationToken(): string | null {
  const active = useImpersonationStore.getState().active;
  if (!active) return null;
  if (Number.isNaN(Date.parse(active.expiresAt)) || Date.parse(active.expiresAt) <= Date.now()) {
    useImpersonationStore.getState().clear();
    return null;
  }
  return active.jwt;
}

/**
 * Impersonation status WITHOUT the clear-on-expiry side effect — the exact
 * twin of `firmActStatus()` in firm-act-store.ts, and read by the same guard
 * (/app/_layout.tsx) for the same reason.
 *
 * 2026-08-25: this accessor did not exist, and that is why impersonation was
 * DEAD end-to-end. A super_admin who started an impersonation was navigated to
 * /app/dashboard (superadmin/tenants/$id-screen.tsx's ImpersonateModal), where
 * the /app guard's firm-staff exit-bounce — which only ever excepted an ACTIVE
 * act-as — saw `super_admin` (a FIRM_STAFF role) with no act-as context and
 * sent them straight back to /admin/client-book. The ImpersonationBanner is
 * mounted on /app/* and could therefore never render, and "Leave impersonation"
 * was unreachable while a live impersonation token was being sent by api().
 *
 * `expired` is reported separately (never folded into `active`) so a guard can
 * refuse to treat a dead token as a credential, exactly as the act-as twin does.
 */
export function impersonationStatus(): 'none' | 'active' | 'expired' {
  const active = useImpersonationStore.getState().active;
  if (!active) return 'none';
  const exp = Date.parse(active.expiresAt);
  if (Number.isNaN(exp) || exp <= Date.now()) return 'expired';
  return 'active';
}

/**
 * Decode the `impersonatedBy` claim from a JWT without verifying the
 * signature. The banner uses this defensively — it only renders when the
 * active token genuinely carries an impersonatedBy claim. Signature trust is
 * the backend's job; this is a presentation-layer guard, not an auth check.
 */
export function decodeImpersonatedBy(jwt: string): string | null {
  const parts = jwt.split('.');
  if (parts.length !== 3) return null;
  try {
    const payloadPart = parts[1];
    if (!payloadPart) return null;
    const json = atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { impersonatedBy?: unknown };
    return typeof claims.impersonatedBy === 'string' ? claims.impersonatedBy : null;
  } catch {
    return null;
  }
}
